// Legacy Notification Adapter - Bridge between old context-based and new event-driven systems
// ✅ NO MAGIC STRINGS: Use typed enums and constants

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

import debugLogger from "../utils/debugLogger";

import { NotificationCoordinator } from "./NotificationCoordinator";
import {
  NotificationSeverity,
  NotificationEvent,
  NotificationPayload,
  NotificationState,
} from "./types";

// Legacy notification interface for backward compatibility
export interface LegacyNotificationState {
  message: string;
  severity: "success" | "error" | "warning" | "info";
  open: boolean;
  duration?: number;
  timeoutId?: NodeJS.Timeout;
}

export interface LegacyNotificationContextType {
  notification: LegacyNotificationState | null;
  setSnackBarNotification: (
    notification: Partial<LegacyNotificationState>,
  ) => void;
  showNotification: (
    message: string,
    severity?: "success" | "error" | "warning" | "info",
  ) => void;
  clearNotification: () => void;
}

// Legacy context for backward compatibility
const LegacyNotificationContext = createContext<LegacyNotificationContextType>({
  notification: null,
  setSnackBarNotification: () => {},
  showNotification: () => {},
  clearNotification: () => {},
});

// Adapter component that bridges legacy and new systems
export const LegacyNotificationAdapter: React.FC<{
  children: React.ReactNode;
  coordinator?: NotificationCoordinator;
}> = ({ children, coordinator }) => {
  const [legacyNotification, setLegacyNotification] =
    useState<LegacyNotificationState | null>(null);
  const [notificationCoordinator] = useState(
    () => coordinator || new NotificationCoordinator(),
  );

  // Convert legacy severity to new severity
  const convertSeverity = (
    severity: "success" | "error" | "warning" | "info",
  ): NotificationSeverity => {
    switch (severity) {
      case "success":
        return NotificationSeverity.SUCCESS;
      case "error":
        return NotificationSeverity.ERROR;
      case "warning":
        return NotificationSeverity.WARNING;
      case "info":
        return NotificationSeverity.INFO;
      default:
        return NotificationSeverity.INFO;
    }
  };

  // Convert new severity to legacy severity
  const convertLegacySeverity = (
    severity: NotificationSeverity,
  ): "success" | "error" | "warning" | "info" => {
    switch (severity) {
      case NotificationSeverity.SUCCESS:
        return "success";
      case NotificationSeverity.ERROR:
        return "error";
      case NotificationSeverity.WARNING:
        return "warning";
      case NotificationSeverity.INFO:
        return "info";
      default:
        return "info";
    }
  };

  // Legacy notification methods
  const clearLegacyNotification = useCallback(() => {
    if (legacyNotification?.timeoutId) {
      clearTimeout(legacyNotification.timeoutId);
    }
    setLegacyNotification(null);
  }, [legacyNotification]);

  const handleSetSnackbar = useCallback(
    (newNotification: Partial<LegacyNotificationState>) => {
      if (legacyNotification) {
        clearLegacyNotification();
      }

      setTimeout(() => {
        const timeoutId = setTimeout(() => {
          clearLegacyNotification();
        }, newNotification.duration || 5000);

        const notificationState: LegacyNotificationState = {
          message: newNotification.message || "",
          severity: newNotification.severity || "info",
          open: true,
          duration: newNotification.duration || 5000,
          timeoutId,
        };

        setLegacyNotification(notificationState);

        // Bridge to new system
        const newNotificationPayload: NotificationPayload = {
          message: notificationState.message,
          severity: convertSeverity(notificationState.severity),
          duration: notificationState.duration,
        };

        notificationCoordinator.show(newNotificationPayload);
      }, 0);
    },
    [legacyNotification, clearLegacyNotification, notificationCoordinator],
  );

  const showLegacyNotification = useCallback(
    (
      message: string,
      severity: "success" | "error" | "warning" | "info" = "info",
    ) => {
      handleSetSnackbar({
        message,
        severity,
        duration: 5000,
      });
    },
    [handleSetSnackbar],
  );

  // Listen to new notification events and update legacy state
  useEffect(() => {
    const eventBus = notificationCoordinator.getEventBus();

    const handleNotificationShow = (payload: unknown) => {
      const notification = payload as NotificationState;

      // Update legacy state to maintain compatibility
      const legacyState: LegacyNotificationState = {
        message: notification.message,
        severity: convertLegacySeverity(notification.severity),
        open: notification.open,
        duration: notification.duration,
        timeoutId: notification.timeoutId,
      };

      setLegacyNotification(legacyState);
    };

    const handleNotificationHide = (payload: unknown) => {
      const { id: _id } = payload as { id: string };
      if (legacyNotification) {
        clearLegacyNotification();
      }
    };

    const handleNotificationClear = () => {
      clearLegacyNotification();
    };

    // Subscribe to new notification events
    const _showSubscription = eventBus.subscribeGlobal(
      NotificationEvent.NOTIFICATION_SHOW,
      handleNotificationShow,
    );

    const _hideSubscription = eventBus.subscribeGlobal(
      NotificationEvent.NOTIFICATION_HIDE,
      handleNotificationHide,
    );

    const _clearSubscription = eventBus.subscribeGlobal(
      NotificationEvent.NOTIFICATION_CLEAR,
      handleNotificationClear,
    );

    debugLogger.info(
      "notifications",
      "Legacy notification adapter initialized",
    );

    // Cleanup subscriptions
    return () => {
      // Note: EventBus doesn't have unsubscribe method in current implementation
      debugLogger.info("notifications", "Legacy notification adapter cleanup");
    };
  }, [notificationCoordinator, legacyNotification, clearLegacyNotification]);

  const legacyContextValue: LegacyNotificationContextType = {
    notification: legacyNotification,
    setSnackBarNotification: handleSetSnackbar,
    showNotification: showLegacyNotification,
    clearNotification: clearLegacyNotification,
  };

  return (
    <LegacyNotificationContext.Provider value={legacyContextValue}>
      {children}
    </LegacyNotificationContext.Provider>
  );
};

// Legacy hook for backward compatibility
export const useLegacyNotification = (): LegacyNotificationContextType => {
  const context = useContext(LegacyNotificationContext);
  if (!context) {
    throw new Error(
      "useLegacyNotification must be used within a LegacyNotificationAdapter",
    );
  }
  return context;
};

// Export the legacy context for direct use if needed
export { LegacyNotificationContext };
